/**
 * Agents — live agent directory with wallet balances, KYC status,
 * suspend/reactivate actions and a detail drawer.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import {
  useAdminQuery,
  useDebouncedValue,
  downloadCsv,
  shortDateTime,
  tzs,
  errorMessage,
} from "../lib/admin";
import { adminApi } from "../lib/api";
import type { AgentDetail, AgentRow } from "../lib/types";
import type { Paged } from "../lib/admin";
import {
  Search,
  Download,
  ShieldCheck,
  ShieldOff,
  UserX,
  UserCheck,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const kycBadge: Record<string, string> = {
  approved: "ev-badge-mint",
  rejected: "ev-badge-red",
  submitted: "ev-badge-amber",
  under_review: "ev-badge-amber",
  info_required: "ev-badge-amber",
  not_started: "ev-badge-blue",
};

export function Agents() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{
    action: "suspend" | "reactivate";
    id: string;
    name: string;
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const q = useDebouncedValue(search);
  const list = useAdminQuery<Paged<AgentRow>>(
    ["agents", q, statusFilter, page],
    "/admin/v1/agents",
    { q: q || undefined, status: statusFilter === "all" ? undefined : statusFilter, page, page_size: 25 },
  );
  const detail = useAdminQuery<AgentDetail>(
    ["agent", selectedId],
    `/admin/v1/agents/${selectedId}`,
    {},
    Boolean(selectedId),
  );

  const mutation = useMutation({
    mutationFn: (input: { action: "suspend" | "reactivate"; id: string; reason?: string }) =>
      adminApi.post(`/admin/v1/agents/${input.id}/${input.action}`, undefined, {
        query: input.action === "suspend" ? { reason: input.reason ?? "" } : {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      setConfirmState(null);
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.meta?.total ?? list.data?.total ?? rows.length;
  const pageSize = list.data?.meta?.page_size ?? 25;

  const exportCsv = () => {
    downloadCsv(
      "evocha-agents.csv",
      ["Code", "Business", "Phone", "Tier", "Status", "KYC", "Available", "Reserved", "Commission", "Total sales"],
      rows.map((a) => [
        a.code,
        a.business_name,
        a.phone,
        a.tier,
        a.status,
        a.kyc_status,
        a.available_minor / 100,
        a.reserved_minor / 100,
        a.commission_minor / 100,
        a.total_sales_minor / 100,
      ]),
    );
  };

  return (
    <>
      <PageHead
        title="Agents"
        description="Agent directory, tiers, KYC status, and account management."
        actions={
          <button className="ev-btn ev-btn-sm ev-btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
            <Download size={16} /> Export
          </button>
        }
      />

      <div className="admin-card-header admin-filter-bar" style={{ marginBottom: 16 }}>
        <div className="admin-filters" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ev-slate-400)", pointerEvents: "none" }} />
            <input
              className="ev-input"
              placeholder="Search name, code, phone…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ paddingLeft: 36 }}
            />
          </div>
          <select className="ev-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <ShieldOff size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {list.isLoading && <LoadingCard label="Loading agents…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && rows.length === 0 && <EmptyState>No agents match these filters.</EmptyState>}

      {rows.length > 0 && (
        <>
          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-table-th">Agent</th>
                    <th className="admin-table-th">Phone</th>
                    <th className="admin-table-th">Tier</th>
                    <th className="admin-table-th">KYC</th>
                    <th className="admin-table-th">Available</th>
                    <th className="admin-table-th">Total sales</th>
                    <th className="admin-table-th">Last login</th>
                    <th className="admin-table-th">Status</th>
                    <th className="admin-table-th" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className="admin-table-row-hover">
                      <td className="admin-table-td" data-label="Agent">
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span className="ev-avatar ev-avatar-sm">{a.business_name.charAt(0).toUpperCase()}</span>
                          <div>
                            <div className="ev-body-semibold">{a.business_name || a.name}</div>
                            <div className="ev-body-micro muted">{a.code} · {a.region || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-table-td" data-label="Phone"><span className="ev-body-mono">{a.phone}</span></td>
                      <td className="admin-table-td" data-label="Tier"><span className="ev-body">{a.tier}</span></td>
                      <td className="admin-table-td" data-label="KYC">
                        <span className={`ev-badge ${kycBadge[a.kyc_status] ?? "ev-badge-blue"}`}>
                          {a.kyc_status === "approved" ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                          {a.kyc_status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="admin-table-td" data-label="Available"><span className="ev-body-mono ev-body-semibold">{tzs(a.available_minor)}</span></td>
                      <td className="admin-table-td" data-label="Total sales"><span className="ev-body">{tzs(a.total_sales_minor)}</span></td>
                      <td className="admin-table-td" data-label="Last login"><span className="ev-body-micro muted">{shortDateTime(a.last_login_at)}</span></td>
                      <td className="admin-table-td" data-label="Status">
                        <span className={`ev-badge ${a.status === "active" ? "ev-badge-mint" : a.status === "suspended" ? "ev-badge-red" : "ev-badge-amber"}`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="admin-table-td" data-label="">
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="ev-btn ev-btn-sm ev-btn-ghost" title="View profile" onClick={() => setSelectedId(a.id)}>
                            <Eye size={16} />
                          </button>
                          <button
                            className="ev-btn ev-btn-sm ev-btn-ghost"
                            title={a.status === "suspended" ? "Reactivate" : "Suspend"}
                            onClick={() =>
                              setConfirmState({
                                action: a.status === "suspended" ? "reactivate" : "suspend",
                                id: a.id,
                                name: a.business_name || a.name,
                              })
                            }
                          >
                            {a.status === "suspended" ? <UserCheck size={16} /> : <UserX size={16} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {total > pageSize && (
            <div className="admin-pagination">
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft size={16} /> Previous
              </button>
              <span className="ev-body-micro">Page {page} · {total} agents</span>
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {selectedId && (
        <div className="admin-drawer-overlay" onClick={() => setSelectedId(null)}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()} style={{ width: 480, maxWidth: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ marginBottom: 4 }}>{detail.data?.business_name ?? "Agent"}</h3>
                <span className="ev-body-micro muted">{detail.data?.code}</span>
              </div>
              <button className="ev-icon-btn" onClick={() => setSelectedId(null)} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            {detail.isLoading && <div style={{ marginTop: 24 }}><LoadingCard label="Loading agent…" /></div>}
            {detail.error && <div style={{ marginTop: 24 }}><ErrorCard error={detail.error} /></div>}

            {detail.data && (
              <div style={{ marginTop: 24 }}>
                <div className="admin-detail-grid">
                  <div>
                    <div className="ev-body-micro muted">Phone</div>
                    <div className="ev-body-mono">{detail.data.phone}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">Status</div>
                    <div className="ev-body">{detail.data.status}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">KYC</div>
                    <div className="ev-body">{detail.data.kyc_status.replace("_", " ")}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">Region</div>
                    <div className="ev-body">{detail.data.region || "—"}{detail.data.district ? `, ${detail.data.district}` : ""}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">Daily limit</div>
                    <div className="ev-body">{tzs(detail.data.daily_limit_minor)}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">Txn limit</div>
                    <div className="ev-body">{tzs(detail.data.txn_limit_minor)}</div>
                  </div>
                </div>

                {detail.data.wallet && (
                  <>
                    <h4 style={{ marginTop: 24, marginBottom: 8 }}>Wallet</h4>
                    <div className="admin-detail-grid">
                      <div>
                        <div className="ev-body-micro muted">Available</div>
                        <div className="ev-body-semibold" style={{ color: "var(--ev-mint-600)" }}>{detail.data.wallet.available_display}</div>
                      </div>
                      <div>
                        <div className="ev-body-micro muted">Reserved</div>
                        <div className="ev-body" style={{ color: "var(--ev-amber-600)" }}>{detail.data.wallet.reserved_display}</div>
                      </div>
                      <div>
                        <div className="ev-body-micro muted">Commission</div>
                        <div className="ev-body" style={{ color: "var(--ev-blue-600)" }}>{detail.data.wallet.commission_display}</div>
                      </div>
                    </div>
                  </>
                )}

                <h4 style={{ marginTop: 24, marginBottom: 8 }}>Recent orders</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {detail.data.orders.length === 0 && <span className="ev-body-micro muted">No orders yet.</span>}
                  {detail.data.orders.slice(0, 8).map((o) => (
                    <div key={o.ref} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span className="ev-body-mono ev-body-micro">{o.ref}</span>
                      <span className="ev-body-micro">{o.amount_display} · {o.status_label}</span>
                    </div>
                  ))}
                </div>

                <h4 style={{ marginTop: 24, marginBottom: 8 }}>Timeline</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {detail.data.timeline.length === 0 && <span className="ev-body-micro muted">No audit events.</span>}
                  {detail.data.timeline.slice(0, 10).map((event) => (
                    <div key={event.id}>
                      <div className="ev-body-micro">{event.action.replaceAll(".", " · ").replaceAll("_", " ")}</div>
                      <div className="ev-body-micro muted">{shortDateTime(event.occurred_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirmState)}
        onClose={() => setConfirmState(null)}
        onConfirm={() => {
          if (!confirmState) return;
          mutation.mutate({ ...confirmState, reason: "Suspended from admin portal" });
        }}
        variant={confirmState?.action === "suspend" ? "danger" : undefined}
        title={confirmState?.action === "suspend" ? "Suspend agent?" : "Reactivate agent?"}
        message={
          confirmState?.action === "suspend"
            ? `Suspend ${confirmState?.name}? They will lose access immediately.`
            : `Reactivate ${confirmState?.name}? They will regain dashboard access.`
        }
        confirmLabel={confirmState?.action === "suspend" ? "Suspend" : "Activate"}
        cancelLabel="Cancel"
      />
    </>
  );
}
